import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import toast from "react-hot-toast";
import { supabase, enhancedSupabase } from "@/lib/supabase";
import { formatPhoneNumber } from "@/lib/utils";

// Create a simplified wrapper for toast with info method
const customToast = {
  success: (message: string) => toast.success(message),
  error: (message: string) => toast.error(message),
  info: (message: string) => toast(message, {
    style: {
      border: '1px solid rgba(59, 130, 246, 0.2)',
      borderLeft: '4px solid #3b82f6',
      background: '#FFFFFF',
      color: '#1e3a8a',
    },
    icon: 'ℹ️',
  })
};

// Define types for our data
export interface Visit {
  id: string;
  date: string;
}

export interface Reward {
  id: string;
  name: string;
  description: string;
  type: "visit_based";
  visitsRequired: number;
  active: boolean;
  created: string;
}

export interface Customer {
  id: string;
  phoneNumber: string;
  name?: string;
  email?: string;
  visits: Visit[];
  redeemedRewards: {
    rewardId: string;
    date: string;
  }[];
}

export interface User {
  username: string;
  role: "admin" | "manager" | "staff";
}

export interface AppContextType {
  customers: Customer[];
  currentUser: User | null;
  rewards: Reward[];
  isInitializing: boolean;
  addCustomerVisit: (customerId: string, customDate?: Date) => Promise<boolean>;
  addCustomerByPhone: (phoneNumber: string, name?: string, email?: string) => Promise<string>;
  getCustomer: (identifier: string) => Customer | undefined;
  getCustomerByPhone: (phoneNumber: string) => Customer | undefined;
  getCustomerById: (id: string) => Customer | undefined;
  getVisitCount: (customerId: string) => number;
  getCustomerRewards: (customerId: string) => Reward[];
  isEligibleForReward: (customerId: string, rewardId: string) => boolean;
  redeemReward: (customerId: string, rewardId: string, bypassEligibilityCheck?: boolean) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  hasPermission: (permission: "manage_customers" | "manage_rewards" | "manage_staff" | "view_reports") => boolean;
  wipeAllData: () => Promise<void>;
  editCustomer: (customerId: string, updatedCustomer: Partial<Customer>) => Promise<boolean>;
  deleteCustomer: (customerId: string) => Promise<boolean>;
  createReward: (reward: Omit<Reward, "id" | "created">) => Promise<string>;
  updateReward: (id: string, updatedReward: Partial<Reward>) => Promise<boolean>;
  deleteReward: (id: string) => Promise<boolean>;
  generateId: () => string;
  notifyStaffOfScan: (customerId: string) => Promise<boolean>;
  loadInitialData: () => Promise<void>;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};

const DEFAULT_ADMIN = { username: "admin", password: "admin123", role: "admin" as const };
const DEFAULT_MANAGER = { username: "manager", password: "manager123", role: "manager" as const };
const DEFAULT_STAFF = { username: "staff", password: "staff123", role: "staff" as const };

const DEFAULT_REWARDS: Reward[] = [
  {
    id: "free-entree-weekly",
    name: "Free Entrée",
    description: "Get a free entrée when you visit twice in the same week (Monday-Sunday)",
    visitsRequired: 2,
    type: "visit_based",
    active: true,
    created: new Date().toISOString()
  },
  {
    id: "free-dessert-monthly",
    name: "Free Dessert",
    description: "Get a free dessert with your meal when you visit 3 times in a 30-day period",
    visitsRequired: 3,
    type: "visit_based",
    active: true,
    created: new Date().toISOString()
  }
];

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [rewards, setRewards] = useState<Reward[]>(DEFAULT_REWARDS);
  const [isInitializing, setIsInitializing] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          try {
            // Use direct query with service role to avoid RLS issues
            const { data: staffData, error: staffError } = await supabase.rpc('get_staff_user_data', {
              p_user_id: session.user.id
            });

            if (staffData && !staffError) {
              setCurrentUser({
                username: staffData.email,
                role: staffData.role
              });
            }
          } catch (staffError) {
            console.error('Error fetching staff data:', staffError);
          }
        }

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'SIGNED_IN' && session?.user) {
            try {
              // Use direct query with service role to avoid RLS issues
              const { data: staffData, error: staffError } = await supabase.rpc('get_staff_user_data', {
                p_user_id: session.user.id
              });

              if (staffData && !staffError) {
                setCurrentUser({
                  username: staffData.email,
                  role: staffData.role
                });
              }
            } catch (staffError) {
              console.error('Error fetching staff data:', staffError);
            }
          } else if (event === 'SIGNED_OUT') {
            setCurrentUser(null);
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, []);

  // Load data from Supabase on component mount and when currentUser changes
  useEffect(() => {
    if (currentUser) {
      loadInitialData();
    }
  }, [currentUser]);

  const loadInitialData = async () => {
    try {
      // Load customers and their related data with a single join query
      const { data: customersWithRelations, error: customersError } = await supabase
        .from('customers')
        .select(`
          *,
          visits(*),
          redeemed_rewards(*)
        `);

      if (customersError) throw customersError;

      // Transform the data to match our app's structure
      const customersWithDetails = customersWithRelations?.map(customer => ({
        id: customer.id,
        phoneNumber: customer.phone_number,
        name: customer.name || undefined,
        email: customer.email || undefined,
        visits: (customer.visits || [])
          .map(v => ({
            id: v.id,
            date: v.date
          }))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
        redeemedRewards: (customer.redeemed_rewards || [])
          .map(r => ({
            rewardId: r.reward_id,
            date: r.date
          }))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      })) || [];

      setCustomers(customersWithDetails);

      // Load rewards from Supabase or initialize with defaults
      const { data: rewardsData, error: rewardsError } = await supabase
        .from('rewards')
        .select('*')
        .order('created_at', { ascending: false });

      if (rewardsError) {
        // If table doesn't exist, create it and insert default rewards
        await supabase.from('rewards').insert(DEFAULT_REWARDS.map(reward => ({
          id: reward.id,
          name: reward.name,
          description: reward.description,
          type: reward.type,
          visits_required: reward.visitsRequired,
          active: reward.active,
          created_at: reward.created
        })));
        setRewards(DEFAULT_REWARDS);
      } else if (rewardsData && rewardsData.length > 0) {
        setRewards(rewardsData.map(reward => ({
          id: reward.id,
          name: reward.name,
          description: reward.description,
          type: reward.type as "visit_based",
          visitsRequired: reward.visits_required,
          active: reward.active,
          created: reward.created_at
        })));
      } else {
        // If no rewards exist, insert defaults
        await supabase.from('rewards').insert(DEFAULT_REWARDS.map(reward => ({
          id: reward.id,
          name: reward.name,
          description: reward.description,
          type: reward.type,
          visits_required: reward.visitsRequired,
          active: reward.active,
          created_at: reward.created
        })));
        setRewards(DEFAULT_REWARDS);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      customToast.error('Failed to load data');
    }
  };

  // Generate a UUID v4
  const generateId = (): string => {
    return crypto.randomUUID();
  };

  // Get customer specifically by phone number
  const getCustomerByPhone = (phoneNumber: string): Customer | undefined => {
    const formattedNumber = phoneNumber.replace(/\D/g, "");
    // First try to find in local state
    const localCustomer = customers.find(customer => customer.phoneNumber === formattedNumber);
    if (localCustomer) return localCustomer;
    
    // If not found in local state, try to fetch from Supabase
    return undefined;
  };

  // Add a new customer or get existing one by phone number
  const addCustomerByPhone = async (phoneNumber: string, name?: string, email?: string): Promise<string> => {
    const formattedNumber = phoneNumber.replace(/\D/g, "");
    
    if (formattedNumber.length < 10) {
      customToast.error("Please enter a valid phone number");
      return "";
    }
    
    try {
    // Check if customer exists
      const { data: existingCustomers, error: lookupError } = await supabase
        .from('customers')
        .select('*')
        .eq('phone_number', formattedNumber);

      if (lookupError) {
        throw lookupError;
      }

      // If customer exists, return their ID
      if (existingCustomers && existingCustomers.length > 0) {
        console.log("[AppContext] Existing customer found:", existingCustomers[0]);
        
        // Try to update local state, but don't fail if it errors
        try {
          await loadInitialData();
        } catch (refreshError) {
          console.error('[AppContext] Error refreshing data, but customer exists:', refreshError);
        }
        
        return existingCustomers[0].id;
      }
      
      // If customer doesn't exist, create new customer
      const { data: newCustomer, error: insertError } = await supabase
        .from('customers')
        .insert({
          phone_number: formattedNumber,
          name: name || null,
          email: email || null,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (insertError) {
        throw insertError;
      }
      
      if (!newCustomer) {
        // This is a rare case where insert succeeded but didn't return data
        // Try to fetch the newly created customer
        const { data: createdCustomer, error: fetchError } = await supabase
          .from('customers')
          .select('*')
          .eq('phone_number', formattedNumber)
          .single();
          
        if (fetchError || !createdCustomer) {
          throw new Error('Customer was created but could not be retrieved');
        }
        
        // Try to update local state, but don't fail if it errors
        try {
          await loadInitialData();
        } catch (refreshError) {
          console.error('[AppContext] Error refreshing data, but customer was created:', refreshError);
        }
        
        console.log("[AppContext] Created and fetched customer:", createdCustomer);
        customToast.success("New customer added!");
        return createdCustomer.id;
      }
      
      // We have a new customer with data
      console.log("[AppContext] New customer created:", newCustomer);
      
      // Try to update local state, but don't fail if it errors
      try {
        await loadInitialData();
      } catch (refreshError) {
        console.error('[AppContext] Error refreshing data, but customer was created:', refreshError);
      }
      
      customToast.success("New customer added!");
      return newCustomer.id;
    } catch (error) {
      console.error('[AppContext] Error adding customer:', error);
      customToast.error('Failed to add customer');
      return "";
    }
  };

  // Helper function to get the week number for a date
  const getWeekNumber = (date: Date): number => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Helper function to check if two dates are in the same week (Monday-Sunday)
  const areInSameWeek = (date1: string, date2: string): boolean => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    
    // Get the Monday of each date's week
    const getMondayOfWeek = (date: Date): Date => {
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday (0)
      const monday = new Date(date);
      monday.setDate(diff);
      monday.setHours(0, 0, 0, 0);
      return monday;
    };
    
    const monday1 = getMondayOfWeek(d1);
    const monday2 = getMondayOfWeek(d2);
    
    return monday1.getTime() === monday2.getTime();
  };

  // Helper function to check if a date is within the past 30 days
  const isWithinPast30Days = (date: string): boolean => {
    const d = new Date(date);
    const now = new Date();
    const timeDiff = now.getTime() - d.getTime();
    const daysDiff = timeDiff / (1000 * 3600 * 24);
    return daysDiff <= 30;
  };

  // Helper function to get earned but unclaimed rewards for a week
  const getEarnedRewardsForWeek = (visits: Visit[], reward: Reward, weekKey: string): number => {
    const visitsInWeek = visits.filter(visit => {
      const visitDate = new Date(visit.date);
      const visitWeekKey = `${visitDate.getFullYear()}-${getWeekNumber(visitDate)}`;
      return visitWeekKey === weekKey;
    });

    return Math.floor(visitsInWeek.length / reward.visitsRequired);
  };

  // Add visit for a customer by their ID
  const addCustomerVisit = async (customerId: string, customDate?: Date): Promise<boolean> => {
    try {
      const { data: customer } = await supabase
        .from('customers')
        .select()
        .eq('id', customerId)
        .single();

      if (!customer) {
        customToast.error("Customer not found");
        return false;
      }

      // Only check for existing visits if we're not using a custom date (admin testing)
      if (!customDate) {
        // Check if they already visited today
        const today = new Date().toISOString().split('T')[0];
        const { data: existingVisit } = await supabase
          .from('visits')
          .select()
          .eq('customer_id', customerId)
          .gte('date', today)
          .lt('date', new Date(new Date().setDate(new Date().getDate() + 1)).toISOString())
          .single();

        if (existingVisit) {
          customToast.info("This customer has already visited today");
          return false;
        }
      }

      // Use custom date if provided, otherwise use current date
      const visitDate = customDate ? customDate.toISOString() : new Date().toISOString();
      const visitId = generateId();

      // Optimistically update the UI
      const updatedCustomers = customers.map(c => {
        if (c.id === customerId) {
          return {
            ...c,
            visits: [
              { id: visitId, date: visitDate },
              ...c.visits
            ]
          };
        }
        return c;
      });
      
      setCustomers(updatedCustomers);
      customToast.success("Visit recorded successfully!");

      // Add new visit to the database
      const { error } = await supabase
        .from('visits')
        .insert({
          id: visitId,
          customer_id: customerId,
          date: visitDate
        });

      if (error) {
        // If there's an error, revert the optimistic update
        await loadInitialData();
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error adding visit:', error);
      customToast.error('Failed to record visit');
      return false;
    }
  };

  // Get customer by ID or phone number
  const getCustomer = (identifier: string): Customer | undefined => {
    // First try to find by ID
    let customer = customers.find(c => c.id === identifier);
    
    // If not found, try by phone number (could be passed a formatted number)
    if (!customer) {
      const formattedNumber = identifier.replace(/\D/g, "");
      customer = customers.find(c => c.phoneNumber === formattedNumber);
    }
    
    return customer;
  };

  // Get customer specifically by ID
  const getCustomerById = (id: string): Customer | undefined => {
    return customers.find(customer => customer.id === id);
  };

  // Get visit count
  const getVisitCount = (customerId: string): number => {
    const customer = getCustomer(customerId);
    if (!customer) return 0;
    return customer.visits.length;
  };

  // Get customer rewards they are eligible for
  const getCustomerRewards = (customerId: string): Reward[] => {
    const customer = getCustomerById(customerId);
    if (!customer) return [];

    // Get visits within the past 30 days
    const recentVisits = customer.visits.filter(visit => isWithinPast30Days(visit.date));

    // Get all visits grouped by week
    const visitsByWeek: { [weekKey: string]: Visit[] } = {};
    recentVisits.forEach(visit => {
      const visitDate = new Date(visit.date);
      // Use Monday-based week key
      const day = visitDate.getDay();
      const diff = visitDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday (0)
      const monday = new Date(visitDate);
      monday.setDate(diff);
      const weekKey = `${monday.getFullYear()}-${monday.getMonth() + 1}-${monday.getDate()}`;
      
      if (!visitsByWeek[weekKey]) {
        visitsByWeek[weekKey] = [];
      }
      visitsByWeek[weekKey].push(visit);
    });

    // Find active rewards and calculate stacked rewards
    const stackedRewards: Reward[] = [];
    
    rewards.filter(reward => reward.active).forEach(reward => {
      // Get redeemed rewards for the past 30 days
      const redeemedRecently = customer.redeemedRewards.filter(r => 
        r.rewardId === reward.id && isWithinPast30Days(r.date)
      ).length;

      // Calculate total earned rewards across all weeks in the past 30 days
      let totalEarnedRewards = 0;
      
      // For each week, check if they qualify for the reward
      Object.values(visitsByWeek).forEach(weekVisits => {
        if (weekVisits.length >= reward.visitsRequired) {
          // Calculate how many rewards they earned this week
          const weeklyRewards = Math.floor(weekVisits.length / reward.visitsRequired);
          totalEarnedRewards += weeklyRewards;
        }
      });

      // Calculate how many unclaimed rewards they have
      const unclaimedRewardsCount = Math.max(0, totalEarnedRewards - redeemedRecently);
      
      // Add duplicate rewards for each unclaimed reward
      for (let i = 0; i < unclaimedRewardsCount; i++) {
        stackedRewards.push(reward);
      }
    });

    return stackedRewards;
  };

  // Check if customer is eligible for a specific reward
  const isEligibleForReward = (customerId: string, rewardId: string): boolean => {
    const customer = getCustomerById(customerId);
    if (!customer) return false;

    const reward = rewards.find(r => r.id === rewardId);
    if (!reward || !reward.active) return false;

    // Get redeemed rewards for the past 30 days
    const redeemedRecently = customer.redeemedRewards.filter(r => 
      r.rewardId === rewardId && isWithinPast30Days(r.date)
    ).length;

    // Get visits within the past 30 days
    const recentVisits = customer.visits.filter(visit => isWithinPast30Days(visit.date));

    // Get all visits grouped by week
    const visitsByWeek: { [weekKey: string]: Visit[] } = {};
    recentVisits.forEach(visit => {
      const visitDate = new Date(visit.date);
      // Use Monday-based week key
      const day = visitDate.getDay();
      const diff = visitDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday (0)
      const monday = new Date(visitDate);
      monday.setDate(diff);
      const weekKey = `${monday.getFullYear()}-${monday.getMonth() + 1}-${monday.getDate()}`;
      
      if (!visitsByWeek[weekKey]) {
        visitsByWeek[weekKey] = [];
      }
      visitsByWeek[weekKey].push(visit);
    });

    // Calculate total earned rewards across all weeks
    let totalEarnedRewards = 0;
    
    // For each week, check if they qualify for the reward
    Object.values(visitsByWeek).forEach(weekVisits => {
      if (weekVisits.length >= reward.visitsRequired) {
        // Calculate how many rewards they earned this week
        const weeklyRewards = Math.floor(weekVisits.length / reward.visitsRequired);
        totalEarnedRewards += weeklyRewards;
      }
    });

    return totalEarnedRewards > redeemedRecently;
  };

  // Redeem a reward for a customer
  const redeemReward = async (customerId: string, rewardId: string, bypassEligibilityCheck: boolean = false): Promise<boolean> => {
    // Skip eligibility check if bypass flag is set (for admin testing)
    if (!bypassEligibilityCheck && !isEligibleForReward(customerId, rewardId)) {
      customToast.error("Customer is not eligible for this reward");
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('redeemed_rewards')
        .insert({
          customer_id: customerId,
          reward_id: rewardId,
          date: new Date().toISOString()
        });

      if (error) throw error;

      // Get reward and customer details for the log
      const rewardName = rewards.find(r => r.id === rewardId)?.name;
      const customer = getCustomerById(customerId);
      const customerName = customer?.name || formatPhoneNumber(customer?.phoneNumber || '');
      
      // Log the reward redemption
      try {
        await supabase.rpc('insert_staff_notification', {
          p_type: 'reward_redeemed',
          p_message: `${rewardName} redeemed for ${customerName}`,
          p_customer_id: customerId
        });
        console.log('Reward redemption logged successfully');
      } catch (logError) {
        console.error('Error logging reward redemption:', logError);
        // Continue anyway since the redemption was successful
      }

      // Update local state
      await loadInitialData();
    
      customToast.success(`${rewardName} has been redeemed successfully!`);
      return true;
    } catch (error) {
      console.error('Error redeeming reward:', error);
      customToast.error('Failed to redeem reward');
      return false;
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error || !data.user) {
        customToast.error("Invalid email or password");
        return false;
    }
    
      const { data: staffData, error: staffError } = await supabase
        .from('staff')
        .select('role, email')
        .eq('id', data.user.id)
        .single();

      if (staffError || !staffData) {
        customToast.error("Failed to retrieve staff role");
        await supabase.auth.signOut(); // Sign out if we can't get staff data
        return false;
      }

      setCurrentUser({ username: staffData.email, role: staffData.role });
      customToast.success(`Welcome, ${staffData.role.charAt(0).toUpperCase() + staffData.role.slice(1)}!`);
      return true;
    } catch (error) {
      console.error('Error during login:', error);
      customToast.error('Login failed');
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
    setCurrentUser(null);
      setCustomers([]); // Clear local data
      setRewards(DEFAULT_REWARDS); // Reset rewards to defaults
    customToast.info("Logged out successfully");
    } catch (error) {
      console.error('Error during logout:', error);
      customToast.error('Failed to log out');
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      // First verify the current password by trying to sign in
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: currentUser?.username || '',
        password: currentPassword
      });

      if (signInError || !signInData.user) {
        customToast.error("Current password is incorrect");
        return false;
      }

      // If current password is correct, update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        customToast.error("Failed to update password");
        return false;
      }

      customToast.success("Password updated successfully");
      return true;
    } catch (error) {
      console.error('Error changing password:', error);
      customToast.error('Failed to change password');
      return false;
    }
  };

  const hasPermission = (permission: "manage_customers" | "manage_rewards" | "manage_staff" | "view_reports"): boolean => {
    if (!currentUser) return false;
    
    // Admin has all permissions
    if (currentUser.role === "admin") return true;
    
    // Manager permissions
    if (currentUser.role === "manager") {
      return ["manage_customers", "manage_rewards", "view_reports"].includes(permission);
    }
    
    // Staff has limited permissions
    if (currentUser.role === "staff") {
      return ["manage_customers"].includes(permission);
    }
    
    return false;
  };

  const wipeAllData = async (): Promise<void> => {
    if (currentUser?.role !== "admin") {
      customToast.error("Only admins can wipe data");
      return;
    }
    
    try {
      // First get all records to delete
      const { data: redeemedRewards } = await supabase.from('redeemed_rewards').select('id');
      const { data: visits } = await supabase.from('visits').select('id');
      const { data: customers } = await supabase.from('customers').select('id');
      
      // Then delete them if they exist
      if (redeemedRewards && redeemedRewards.length > 0) {
        const rewardIds = redeemedRewards.map(r => r.id);
        await supabase.from('redeemed_rewards').delete().in('id', rewardIds);
      }
      
      if (visits && visits.length > 0) {
        const visitIds = visits.map(v => v.id);
        await supabase.from('visits').delete().in('id', visitIds);
      }
      
      if (customers && customers.length > 0) {
        const customerIds = customers.map(c => c.id);
        await supabase.from('customers').delete().in('id', customerIds);
      }

      // Update local state
      setCustomers([]);
      customToast.success("All customer data has been wiped");
    } catch (error) {
      console.error('Error wiping data:', error);
      customToast.error('Failed to wipe data');
    }
  };

  // Edit customer
  const editCustomer = async (customerId: string, updatedCustomer: Partial<Customer>): Promise<boolean> => {
    if (!hasPermission("manage_customers")) {
      customToast.error("You don't have permission to edit customer data");
      return false;
    }
    
    try {
      // Validate the phone number format before submitting
      if (updatedCustomer.phoneNumber && !/^\d+$/.test(updatedCustomer.phoneNumber)) {
        customToast.error("Phone number should contain only digits");
        return false;
      }
      
      const { error } = await supabase
        .from('customers')
        .update({
          phone_number: updatedCustomer.phoneNumber,
          name: updatedCustomer.name || null,
          email: updatedCustomer.email || null
        })
        .eq('id', customerId);
      
      if (error) throw error;

      // Update local state with a proper await
      try {
        await loadInitialData();
        
        customToast.success("Customer updated successfully");
        return true;
      } catch (loadError) {
        console.error('Error refreshing data after update:', loadError);
        // Still return true since the database update was successful
        customToast.success("Customer updated successfully. Please refresh for latest data.");
        return true;
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      customToast.error('Failed to update customer');
      return false;
    }
  };
  
  const deleteCustomer = async (customerId: string): Promise<boolean> => {
    if (!hasPermission("manage_customers")) {
      customToast.error("You don't have permission to delete customer data");
      return false;
    }
    
    try {
      // Find the customer to be deleted
      const customerToDelete = customers.find(c => c.id === customerId);
      if (!customerToDelete) {
        customToast.error("Customer not found");
        return false;
      }
      
      // Optimistically update UI by removing the customer from state
      setCustomers(customers.filter(c => c.id !== customerId));
      customToast.success("Customer deleted successfully");
      
      // Delete customer from Supabase
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      if (error) {
        // If there's an error, revert the optimistic update
        await loadInitialData();
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      customToast.error('Failed to delete customer');
      return false;
    }
  };

  // Reward management
  const createReward = async (reward: Omit<Reward, "id" | "created">): Promise<string> => {
    if (!hasPermission("manage_rewards")) {
      customToast.error("You don't have permission to create rewards");
      return "";
    }
    
    try {
      const newReward = {
        id: generateId(),
        name: reward.name,
        description: reward.description,
        type: reward.type,
        visits_required: reward.visitsRequired,
        active: reward.active,
        created_at: new Date().toISOString()
    };
    
      const { error } = await supabase
        .from('rewards')
        .insert(newReward);

      if (error) throw error;

      // Update local state
      await loadInitialData();
      
    customToast.success(`New reward "${reward.name}" created`);
      return newReward.id;
    } catch (error) {
      console.error('Error creating reward:', error);
      customToast.error('Failed to create reward');
      return "";
    }
  };
  
  const updateReward = async (id: string, updatedReward: Partial<Reward>): Promise<boolean> => {
    if (!hasPermission("manage_rewards")) {
      customToast.error("You don't have permission to update rewards");
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('rewards')
        .update({
          name: updatedReward.name,
          description: updatedReward.description,
          type: updatedReward.type,
          visits_required: updatedReward.visitsRequired,
          active: updatedReward.active
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      await loadInitialData();
      
      customToast.success("Reward updated successfully");
      return true;
    } catch (error) {
      console.error('Error updating reward:', error);
      customToast.error('Failed to update reward');
      return false;
    }
  };
  
  const deleteReward = async (id: string): Promise<boolean> => {
    if (!hasPermission("manage_rewards")) {
      customToast.error("You don't have permission to delete rewards");
      return false;
    }
    
    try {
      // Check if the reward is in use
      const { data: redeemedRewards } = await supabase
        .from('redeemed_rewards')
        .select('id')
        .eq('reward_id', id)
        .limit(1);
    
      if (redeemedRewards && redeemedRewards.length > 0) {
      customToast.error("This reward has been used by customers and cannot be deleted");
      return false;
    }
    
      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      await loadInitialData();
      
    customToast.success("Reward deleted successfully");
    return true;
    } catch (error) {
      console.error('Error deleting reward:', error);
      customToast.error('Failed to delete reward');
      return false;
    }
  };

  // Function to notify staff when a customer scans their QR code
  const notifyStaffOfScan = async (customerId: string) => {
    try {
      // Try to fetch from Supabase first
      const { data: customerData, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (error || !customerData) {
        throw new Error('Customer not found');
      }

      // Update local state to include this customer
      await loadInitialData();

      console.log('Notifying staff about customer scan for ID:', customerId);
      
      // Use the RPC function to insert notification (bypasses RLS)
      const { data, error: notificationError } = await supabase.rpc('insert_staff_notification', {
        p_type: 'scan',
        p_message: `Customer ${customerData.name || formatPhoneNumber(customerData.phone_number)} has scanned their QR code`,
        p_customer_id: customerId
      });

      if (notificationError) {
        console.error('Error inserting staff notification:', notificationError);
        throw notificationError;
      }

      console.log('Successfully notified staff about customer scan, notification ID:', data);
      return true;
    } catch (error) {
      console.error('Error notifying staff of scan:', error);
      throw error;
    }
  };

  const value = {
        customers,
        currentUser,
        rewards,
    isInitializing,
        addCustomerVisit,
        addCustomerByPhone,
        getCustomer,
        getCustomerByPhone,
        getCustomerById,
        getVisitCount,
        getCustomerRewards,
        isEligibleForReward,
        redeemReward,
        login,
        logout,
    changePassword,
        hasPermission,
        wipeAllData,
        editCustomer,
        deleteCustomer,
        createReward,
        updateReward,
        deleteReward,
    generateId,
    notifyStaffOfScan,
    loadInitialData,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
